import * as React from 'react';
import { IField as IFieldMark, IToken } from '../types/my-types';
import { IFieldReview } from '../components/review/form/FieldList';
import { IValidationErrorByExtraction, TValidationType } from '../types/validation-types';
import { NotWorthIt, FixMe } from '../types/util';
import { useImmerReducer } from 'use-immer';
import { IRevertData } from '../components/spreadsheet/SpreadsheetTypes';
import { mergeValidationSummaries } from '../utils/validationUtils';

interface IMarkState {
  pageIndex: number;
  activeField: string;
  docLabel: string;
  tokenizerVersion: string;
  tokens: any[];
  correctionsInARowCount: number;
  newFieldFocused: boolean;
  isSaving: boolean;
  showValidations: boolean;
  visibleExtractionIds: string[];
  validationSummaryByExtractionId: IValidationErrorByExtraction[] | [];
  misMatchedIndexTokens: IToken[];
  validationsEnabled: boolean;
  gridShowValidationErrors: TValidationType[];
  selectedFields: string[];
  selectedFieldsInfoMark: IFieldMark[];
  selectedFieldsInfoReview: IFieldReview[];
  showDuplicateModal: boolean;
  showFieldCheckboxes: boolean;
  isErrorIgnored: boolean;
  prefetchStatus: null | 'loading' | 'done' | 'error';
  spreadsheetFocusedCell: { rowId: string; colId: string } | null;
  fieldListFocused: boolean; // is the field in the field list the focused element?
}

const initialState: IMarkState = {
  pageIndex: 0,
  activeField: '',
  docLabel: '',
  tokenizerVersion: '',
  tokens: [],
  correctionsInARowCount: 0,
  newFieldFocused: false,
  isSaving: false,
  showValidations: true,
  visibleExtractionIds: [],
  validationSummaryByExtractionId: [],
  misMatchedIndexTokens: [],
  validationsEnabled: true,
  gridShowValidationErrors: [],
  selectedFields: [],
  selectedFieldsInfoMark: [],
  selectedFieldsInfoReview: [],
  showDuplicateModal: false,
  showFieldCheckboxes: false,
  isErrorIgnored: false,
  prefetchStatus: null,
  spreadsheetFocusedCell: null,
  fieldListFocused: false,
};

function markReducer(draft: FixMe, action: NotWorthIt) {
  switch (action.type) {
    case 'ADDING_NEW_FIELD':
      draft.newFieldFocused = true;
      draft.activeField = '';
      return;
    case 'SET_SPREADSHEET_FOCUSED_CELL':
      draft.spreadsheetFocusedCell = action.payload;
      draft.activeField = action.payload.colId;

      if (action.payload?.fieldListFocused !== undefined) {
        draft.fieldListFocused = action.payload.fieldListFocused;
      }

      if (action.payload.pageIndex >= 0) {
        draft.pageIndex = action.payload.pageIndex;
      } else if (action.payload.pageIndex === -1) {
        draft.pageIndex = 0;
      }

      return;
    case 'UPDATE_CORRECTIONS_COUNT':
      draft.correctionsInARowCount = action.payload;
      return;
    case 'INCREMENT_CORRECTIONS_COUNT':
      draft.correctionsInARowCount += 1;
      return;
    case 'PAGE_INDEX':
      draft.pageIndex = action.payload;
      return;
    case 'FOCUS_FIELD':
      draft.newFieldFocused = false;
      draft.activeField = action.payload.field;

      if (action.payload?.fieldListFocused !== undefined) {
        draft.fieldListFocused = action.payload.fieldListFocused;
      }

      if (action.payload.pageIndex >= 0) {
        draft.pageIndex = action.payload.pageIndex;
      }

      return;
    case 'RESET_TOKENS':
      draft.tokens = [];
      return;
    case 'SET_TOKENS':
      draft.tokens = action.tokens;
      return;
    case 'ADD_TOKEN':
      return void draft.tokens.push(action.token);
    case 'REMOVE_TOKEN':
      return void draft.tokens.splice(
        draft.tokens.findIndex((token: FixMe) => token.index === action.index),
        1
      );
    case 'IS_SAVING':
      draft.isSaving = action.payload.isSaving;
      return;
    case 'SHOW_VALIDATIONS':
      draft.showValidations = action.payload;
      return;
    case 'GRID_SHOW_VALIDATION_ERRORS':
      draft.gridShowValidationErrors = action.payload;
      return;
    case 'SAVE_FIELD':
      draft.tokens = [];
      if (action?.payload?.nextActiveField) {
        draft.activeField = action.payload.nextActiveField;
      } else {
        draft.activeField = '';
      }

      if (action.payload?.fieldListFocused !== undefined) {
        draft.fieldListFocused = action.payload.fieldListFocused;
      }

      draft.newFieldFocused = false;
      return;
    case 'DOC_LABEL':
      draft.docLabel = action.payload;
      return;
    case 'SET_VISIBLE_EXTRACTIONS':
      draft.visibleExtractionIds = action.payload;
      return;
    case 'RESET_PAGE':
      draft.tokens = [];

      draft.newFieldFocused = false;
      // If we are not in review, reset the active field/page index and spreadsheet focused cell
      if (action?.payload?.resetFieldAndPage) {
        draft.activeField = '';
        draft.pageIndex = 0;
        draft.spreadsheetFocusedCell = null;
      }
      return;
    case 'TOKENIZER_VERSION':
      draft.tokenizerVersion = action.payload;
      return;
    case 'SET_MISMATCHED_INDEX_TOKENS':
      draft.misMatchedIndexTokens = action.payload;
      return;
    case 'VALIDATIONS_ENABLED':
      draft.validationsEnabled = action.payload;
      return;
    case 'SET_VALIDATION_SUMMARY_BY_EXTRACTION_ID':
      // Merge incoming validation with existing (additive update)
      // This allows extracted and transformed validation to coexist
      // datasetTypesToClear specifies which dataset types to clear before merging
      // (important when a dataset type has no errors - we still need to clear old errors)

      draft.validationSummaryByExtractionId = mergeValidationSummaries(
        draft.validationSummaryByExtractionId,
        action.payload,
        action.datasetTypesToClear
      );
      return;
    case 'CLEAR_VALIDATION_SUMMARY':
      // Use this when you need to completely reset validation state
      // (e.g., when switching to a different extraction batch)
      draft.validationSummaryByExtractionId = [];
      return;
    case 'SET_SELECTED_FIELDS':
      draft.selectedFields = action.payload;
      return;
    case 'SET_SELECTED_FIELDS_INFO':
      if (action.isReview) {
        draft.selectedFieldsInfoReview = [...draft.selectedFieldsInfoReview, ...action.payload];
      } else {
        draft.selectedFieldsInfoMark = [...draft.selectedFieldsInfoMark, ...action.payload];
      }
      return;
    case 'REMOVE_SELECTED_FIELD_INFO':
      if (action.isReview) {
        draft.selectedFieldsInfoReview = draft.selectedFieldsInfoReview.filter(
          (field: IFieldReview) => !action.payload.includes(field.label)
        );
      } else {
        draft.selectedFieldsInfoMark = draft.selectedFieldsInfoMark.filter(
          (field: IFieldMark) => !action.payload.includes(field.id)
        );
      }
      return;
    case 'SET_DUPLICATE_MODAL':
      draft.showDuplicateModal = action.payload;
      return;
    case 'SET_FIELD_CHECKBOXES':
      draft.showFieldCheckboxes = action.payload;
      return;
    case 'FIELD_DUPLICATION_SUCCESS':
      draft.selectedFields = [];
      draft.showFieldCheckboxes = false;
      draft.showDuplicateModal = false;
      draft.selectedFieldsInfoMark = [];
      draft.selectedFieldsInfoReview = [];
      return;
    case 'SET_ERROR_IGNORED':
      draft.isErrorIgnored = action.payload;
      return;
    case 'SET_PREFETCH_STATUS':
      draft.prefetchStatus = action.prefetchStatus;
      return;
    default:
      return;
  }
}

const defaultDispatch: React.Dispatch<any> = () => initialState;

export const MarkStateContext = React.createContext(initialState);
export const MarkDispatchContext = React.createContext<React.Dispatch<any>>(defaultDispatch);

export const MarkContextProvider = (props: NotWorthIt) => {
  // FIXME: Address `any` later
  const [state, dispatch] = useImmerReducer<IMarkState, IMarkState>(markReducer, initialState);
  return (
    <MarkDispatchContext.Provider value={dispatch}>
      <MarkStateContext.Provider value={state}>{props.children}</MarkStateContext.Provider>
    </MarkDispatchContext.Provider>
  );
};
